import {
  GetProductResponse,
  Product as BufProduct,
  CreateProductResponse,
  UpdateProductResponse,
  ListProductsResponse,
} from '@buf/upcyclers_gateway.bufbuild_es/product/v2/product_pb';

import { handleAPIError, InternalError } from '@lib/errors';
import { fetchWrapper } from '@lib/network/fetch-wrapper';
import { handleLog } from '@lib/logging/utils';
import { APIRoutes } from '@lib/routes';
import { CacheTags } from '@lib/tags';

import { Product } from './definitions';

export const ProductService = {
  getProductById: async (productId: string): Promise<Product> => {
    const response = await fetchWrapper(`${APIRoutes.products}/${productId}`, {
      method: 'GET',
    });

    const data = await response.json();

    const errorMessage = 'ProductService.getProductById error';
    if (!response.ok) {
      handleLog('error', {
        message: errorMessage,
        metadata: {
          method: 'GET',
          status: response.status,
          error: data.error,
          productId,
        },
      });
      handleAPIError(data);
    }

    const { product } = GetProductResponse.fromJson(data);

    if (!product) {
      const error = `Failed to retrieve product with ID of ${productId}`;
      handleLog('error', {
        message: errorMessage,
        metadata: {
          error,
          product,
        },
      });
      throw new InternalError(error);
    }

    const { images, videos, ...rest } = product;

    return {
      ...rest,
      images: images.map((image) => ({
        imageUrl: image.imageUrl,
        imageAltText: image.imageAltText,
      })),
      videos: videos.map((video) => ({
        videoId: video.videoId,
        videoAltText: video.videoAltText,
      })),
    };
  },
  addProduct: async (product: Product): Promise<string> => {
    const response = await fetchWrapper(`${APIRoutes.products}`, {
      method: 'POST',
      body: JSON.stringify(new BufProduct({ ...product })),
    });

    const data = await response.json();

    if (!response.ok) {
      handleLog('error', {
        message: 'ProductService.addProduct error',
        metadata: {
          method: 'POST',
          status: response.status,
          error: data.error,
          product,
        },
      });
      handleAPIError(data);
    }

    const { id } = CreateProductResponse.fromJson(data);

    return id;
  },
  updateProduct: async (product: Product): Promise<string> => {
    const response = await fetchWrapper(`${APIRoutes.products}`, {
      method: 'PUT',
      body: JSON.stringify(new BufProduct({ ...product })),
    });

    const data = await response.json();

    if (!response.ok) {
      handleLog('error', {
        message: 'ProductService.updateProduct error',
        metadata: {
          method: 'PUT',
          status: response.status,
          error: data.error,
          product,
        },
      });
      handleAPIError(data);
    }

    const { id } = UpdateProductResponse.fromJson(data);

    return id;
  },
  listProducts: async (
    filters: string,
  ): Promise<{ products: Product[]; totalCount: number }> => {
    const response = await fetchWrapper(`${APIRoutes.products}?${filters}`, {
      method: 'GET',
      next: { tags: [CacheTags.Product] },
    });

    const data = await response.json();

    if (!response.ok) {
      handleLog('error', {
        message: 'ProductService.listProducts error',
        metadata: {
          method: 'GET',
          status: response.status,
          error: data.error,
          filters,
        },
      });
      handleAPIError(data);
    }

    const { products, totalCount } = ListProductsResponse.fromJson(data);

    return {
      products: products.map((product) => {
        const { images, videos, ...rest } = product;

        return {
          ...rest,
          images: images.map((image) => ({
            imageUrl: image.imageUrl,
            imageAltText: image.imageAltText,
          })),
          videos: videos.map((video) => ({
            videoId: video.videoId,
            videoAltText: video.videoAltText,
          })),
        };
      }),
      totalCount,
    };
  },
  deleteProduct: async (productId: string) => {
    const response = await fetchWrapper(`${APIRoutes.products}/${productId}`, {
      method: 'DELETE',
    });

    const data = await response.json();

    if (!response.ok) {
      handleLog('error', {
        message: 'ProductService.deleteProduct error',
        metadata: {
          method: 'DELETE',
          status: response.status,
          error: data.error,
          productId,
        },
      });
      handleAPIError(data);
    }

    return;
  },
};
